import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { Button, Dropdown } from "react-bootstrap";
import { useAuth } from "../../../../../firebase/AuthProvider";
import TaskGroup from "../../../../../interfaces/task/workflow/TaskGroup";
import TemplateTaskShortResponse from "../../../../../interfaces/templateTask/TemplateTaskShortResponse";
import {
    customBatchRun,
    getAllTemplateTasks,
    getTemplateTasks,
    submitTaskToAddToGroup,
} from "../../../../../services/templateService/TemplateTaskService";
import { formatDateToDateFormFormat } from "../../../../../utilities/dateUtils";
import Banner from "../../../../ui/Banner/Banner";
import LoadingSpinner from "../../../../ui/Loading/LoadingSpinner";
import checklisticon from '../../../../../assets/images/checklisticon.svg';
import editicons from '../../../../../assets/images/editicons.svg';
import backarrowbtn from '../../../../../assets/images/backarrowbtn.svg';
import { formatDistributionText } from "../../../../../utilities/chat/textUtils";
import SearchTasks from "./SearchTasks";
import TemplateTask from "../../../../../interfaces/templateTask/TemplateTask";

interface OptionType {
    value: string;
    label: string;
}

interface RecurringTasksListProps {
    onEditTask: (taskId: number) => void;
}

const RecurringTasksList: React.FC<RecurringTasksListProps> = ({ onEditTask }) => {
    const { currentUser, currentRoles } = useAuth();
    const navigate = useNavigate();

    const [reload, setReload] = useState<boolean>(false);
    const [recurringTasks, setRecurringTasks] = useState<TaskGroup>();
    const [selectedTaskToAdd, setSelectedTaskToAdd] = useState<string>("");
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [selectedTaskToRun, setSelectedTaskToRun] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedEndDateToRun, setSelectedEndDateToRun] = useState<string>(
        formatDateToDateFormFormat(new Date())
    );
    const [selectedStartDateToRun, setSelectedStartDateToRun] = useState<string>(
        formatDateToDateFormFormat(new Date())
    );
    const [addTemplateTaskOptions, setAddTemplateTaskOptions] = useState<OptionType[]>([]);
    const [allTemplateTasks, setAllTemplateTasks] = useState<TemplateTaskShortResponse[]>();
    const [searchQuery, setSearchQuery] = useState<string>("");

    useEffect(() => {
        const handleReload = () => {
            setReload(prev => !prev);
        };
        window.addEventListener('recurring-tasks-updated', handleReload);
        return () => {
            window.removeEventListener('recurring-tasks-updated', handleReload);
        };
    }, []);

    useEffect(() => {
        const fetchTaskTemplates = async () => {
            if (currentUser !== null) {
                const authToken: string = await currentUser.getIdToken();
                const fetchedTemplates = await getTemplateTasks(authToken);
                if (fetchedTemplates) setRecurringTasks(fetchedTemplates);
            }
        };

        const fetchAllTaskTemplates = async () => {
            if (currentUser !== null) {
                const authToken: string = await currentUser.getIdToken();
                const fetchedTemplates = await getAllTemplateTasks(authToken);
                if (fetchedTemplates) setAllTemplateTasks(fetchedTemplates);
            }
        };

        if (!allTemplateTasks) {
            fetchAllTaskTemplates();
        }
        if (!recurringTasks) {
            fetchTaskTemplates();
        }
        if (allTemplateTasks) {
            const formattedTemplateTaskOptions = allTemplateTasks.map((task) => ({
                value: task.id.toString(),
                label: task.title,
            }));
            setAddTemplateTaskOptions(formattedTemplateTaskOptions);
        }
    }, [reload, allTemplateTasks, currentUser]);

    const handleAddTaskChange = (selectedOption: SingleValue<OptionType>) => {
        const taskId = selectedOption ? selectedOption.value : "";
        setSelectedTaskToAdd(taskId);
    };

    const handleRunTaskChange = (selectedOption: SingleValue<OptionType>) => {
        const taskId = selectedOption ? selectedOption.value : "";
        setSelectedTaskToRun(taskId);
    };

    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedStartDateToRun(e.target.value);
    };

    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedEndDateToRun(e.target.value);
    };

    const submitTaskToAdd = async () => {
        setIsLoading(true);
        if (selectedTaskToAdd && currentUser) {
            const authToken = await currentUser.getIdToken();
            const response = await submitTaskToAddToGroup(authToken, selectedTaskToAdd);
            if (response) {
                setReload(!reload);
            }
        }
        setIsLoading(false);
    };

    const closeSuccessMessage = () => {
        setSuccessMessage("");
    };

    const submitTaskTrigger = async () => {
        setIsLoading(true);
        if (selectedTaskToRun && currentUser) {
            const authToken = await currentUser.getIdToken();
            const response = await customBatchRun(
                selectedStartDateToRun,
                selectedEndDateToRun,
                selectedTaskToRun,
                authToken
            );
            if (response) {
                if (parseInt(response) > 0) {
                    setSuccessMessage(
                        `${response} tasks were either created or were already created for the selected recurring task`
                    );
                }
                setIsLoading(false);
            }
        }
        setIsLoading(false);
    };

    const backBtnUrl = (): string => {
        if (currentRoles?.some((role) => role.name === "OWNER" || role.name === "ADMIN")) {
            return "/dashboard/owner/settings";
        } else {
            return "/settings";
        }
    }

    const handleSearch = (query: string) => {
        setSearchQuery(query.toLowerCase());
    };

    return (
        <div>
            <div className="top top_bar_flx border_tittle">
                <div className="top_bar_tittle">
                    <h2><Link to={backBtnUrl()}><img style={{ width: '1.5rem' }} src={backarrowbtn} alt="" /></Link>Recurring Tasks</h2>
                </div>
                <div className="top_right_flx">
                    <Button variant="primary" className="btn_sm" onClick={() => navigate("/addtask/new/recurring")}>New Recurring Task</Button>
                </div>
            </div>
            {successMessage !== "" && (
                <div>
                    <Banner
                        status="success"
                        message={successMessage}
                        dismissBanner={closeSuccessMessage}
                    />
                </div>
            )}
            <div className="">
                {recurringTasks &&
                    Object.entries(recurringTasks).map(([taskGroup, groupData]) => {
                        const filteredTasks = groupData.templateTasks.filter(task => {
                            const distributionText = formatDistributionText(task as unknown as TemplateTask);
                            return (
                                task.title.toLowerCase().includes(searchQuery) ||
                                distributionText.toLowerCase().includes(searchQuery)
                            );
                        });

                        return (
                            <div key={taskGroup}>
                                <div className="top top_bar_flx">
                                    <div className="top_bar_tittle">
                                        <SearchTasks onSearch={handleSearch} />
                                    </div>
                                    <div className="top_right_flx">
                                        <Dropdown className="my_dropdown my_dropdown_primary my_dropdown_secondary" autoClose="outside">
                                            <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                                                Assign New Task
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu align="end" className="py-2" style={{ minWidth: '300px' }}>
                                                {allTemplateTasks && (
                                                    <div className="custom_input_flled">
                                                        <label htmlFor="requiredFiles">Select task to add to group</label>
                                                        <Select
                                                            className="react-select-container react_slect_sm"
                                                            classNamePrefix="react-select"
                                                            options={addTemplateTaskOptions.filter(
                                                                (option) =>
                                                                    !groupData.templateTasks.some(
                                                                        (task) => task.id == parseInt(option.value)
                                                                    )
                                                            )}
                                                            onChange={handleAddTaskChange}
                                                            value={addTemplateTaskOptions.find(
                                                                (option) => option.value === selectedTaskToAdd
                                                            )}
                                                        />
                                                    </div>
                                                )}

                                                <div className="mt-2">
                                                    {isLoading ? (
                                                        <Button disabled type="button">
                                                            <LoadingSpinner color="white" size="sm" />
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            style={{ minWidth: 'unset' }}
                                                            className="btn_sm w-100"
                                                            variant="primary"
                                                            onClick={submitTaskToAdd}
                                                        >
                                                            Submit
                                                        </Button>
                                                    )}
                                                </div>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                        <Dropdown className="my_dropdown my_dropdown_primary" autoClose="outside">
                                            <Dropdown.Toggle variant="primary" id="dropdown-basic">
                                                Custom Run
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu className="py-2" style={{ minWidth: '300px' }}>
                                                {allTemplateTasks && (
                                                    <>
                                                        <div className="custom_input_flled">
                                                            <label htmlFor="requiredFiles">Select task to execute</label>
                                                            <Select
                                                                className="react-select-container react_slect_sm"
                                                                classNamePrefix="react-select"
                                                                options={addTemplateTaskOptions.filter(
                                                                    (option) => option.value !== selectedTaskToRun
                                                                )}
                                                                onChange={handleRunTaskChange}
                                                                value={addTemplateTaskOptions.find(
                                                                    (option) => option.value === selectedTaskToAdd
                                                                )}
                                                            />
                                                        </div>
                                                        <div className="custom_input_flled">
                                                            <label htmlFor="startDate">Select Start Date for execution</label>
                                                            <input
                                                                style={{ maxWidth: 'unset' }}
                                                                type="date"
                                                                id="startDate"
                                                                name="startDate"
                                                                value={selectedStartDateToRun}
                                                                onChange={handleStartDateChange}
                                                                className="form-control sm_input"
                                                            />
                                                        </div>

                                                        <div className="custom_input_flled">
                                                            <label htmlFor="endDate">Select End Date for execution</label>
                                                            <input
                                                                style={{ maxWidth: 'unset' }}
                                                                type="date"
                                                                id="endDate"
                                                                name="endDate"
                                                                value={selectedEndDateToRun}
                                                                onChange={handleEndDateChange}
                                                                className="form-control sm_input"
                                                            />
                                                        </div>

                                                        <div className="">
                                                            {isLoading ? (
                                                                <Button disabled type="button" className="w-100">
                                                                    <LoadingSpinner color="white" size="sm" />
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    className="w-100"
                                                                    disabled={isLoading}
                                                                    onClick={!isLoading ? submitTaskTrigger : () => { }}
                                                                >
                                                                    Submit
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </Dropdown.Menu>
                                        </Dropdown>

                                    </div>
                                </div>

                                <div className="recurring_tasks_grid">
                                    {filteredTasks
                                        .slice()
                                        .sort((a, b) => a.id - b.id)
                                        .map((task, index) => (
                                            <div key={index} className="recurring_tasks_card">
                                                <div className="recurring_tasks_card_cntnt">
                                                    <div className="recurring_tasks_img shrink-0">
                                                        <img style={{ width: '1.5rem' }} src={checklisticon} alt="checklisticon" />
                                                    </div>
                                                    <div className="recurring_tasks_card_txt">
                                                        <h3 onClick={() => onEditTask(task.id)} aria-label={task.title} title={task.title}>
                                                            {task.title}
                                                        </h3>
                                                        <p>{formatDistributionText(task as unknown as TemplateTask)}</p>
                                                    </div>
                                                </div>
                                                <button onClick={() => onEditTask(task.id)}>
                                                    <img style={{ width: '1.5rem' }} src={editicons} alt="editicons" />
                                                </button>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )
                    })}
            </div>
        </div>
    );
};

export default RecurringTasksList;
